'use client';

import dynamic from 'next/dynamic';
import { useInView } from 'react-intersection-observer';
import { useLocale } from './LocaleProvider';
import { TextReveal } from './primitives/TextReveal';

/**
 * PortfolioVisualizer — the 3D signature moment, day 7.
 *
 * Lazy loads @react-three/fiber + three only when this section scrolls
 * into view (rootMargin 200px preload). Until then, the user pays zero
 * bytes of Three.js on the initial hero. If the user never scrolls this
 * far, they never download it.
 *
 * Static poster-image fallback is a CSS-only gold-gradient halo so
 * there's always a graceful placeholder while Three boots.
 */

const PortfolioScene = dynamic(
  () => import('./three/PortfolioScene').then((m) => m.PortfolioScene),
  {
    ssr: false,
    loading: () => (
      <div className="absolute inset-0 grid place-items-center">
        <div className="w-10 h-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
      </div>
    ),
  },
);

export function PortfolioVisualizer() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px',
  });

  const copy = isAr
    ? {
        eyebrow: 'المحفظة ثلاثية الأبعاد',
        heading: 'القطع ترتفع — والقيمة ترتفع معها.',
        sub: 'كل عمود طوله يعكس نسبة احتفاظ القطعة بقيمتها. باتيك وروليكس الذهبية المتوهّجة تُحلّق؛ القطع الدارجة تبقى قريبة من الأرض. عرض ثلاثي الأبعاد لما تُخبرك به Sophia بالأرقام.',
      }
    : {
        eyebrow: 'Portfolio · 3D',
        heading: 'The trophies rise. The rest stays low.',
        sub: 'Each column\'s height encodes its value retention. Patek and Daytona blaze gold at the top; drifting pieces sink. It\'s what Sophia tells you in data — now in space.',
      };

  return (
    <section className="relative py-12 sm:py-16 md:py-32 border-t border-white/5 overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-radial-gold opacity-70"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 mb-5 text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {copy.eyebrow}
          </div>
          <TextReveal as="h2" className="font-serif text-display-sm text-cream leading-[0.98]">
            {copy.heading}
          </TextReveal>
          <p className="mt-6 text-fog-300 text-lg leading-relaxed max-w-xl">{copy.sub}</p>
        </div>

        <div
          ref={ref}
          className="relative w-full aspect-[16/9] max-h-[640px] rounded-[28px] overflow-hidden bg-gradient-to-b from-obsidian-800 to-obsidian border border-white/5"
        >
          {/* Gold halo poster while Three.js boots */}
          {!inView && (
            <div className="absolute inset-0 grid place-items-center">
              <div className="w-48 h-48 rounded-full bg-gold-gradient blur-3xl opacity-30" />
            </div>
          )}
          {inView && <PortfolioScene />}

          {/* Bottom gradient for legend/text legibility */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-obsidian-900 to-transparent"
          />

          {/* Legend */}
          <div className="absolute bottom-6 start-6 flex items-center gap-5 text-[11px] text-fog-300 tracking-wide">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-gold" />
              {isAr ? 'استثماري' : 'Trophy'}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-gold-deep" />
              {isAr ? 'مستقر' : 'Stable'}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#8c6a2b]" />
              {isAr ? 'منخفض' : 'Drift'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
